import { useEffect } from 'react';
import {
  syncAdminPushIfAuthenticated,
  sendAdminPushHeartbeat,
  waitForAdminServiceWorkerRegistration,
} from '../services/admin-push.service';
import { getNotificationPermission } from '../utils/notifications';

/**
 * Global admin push sync — keeps admin-scoped subscription alive for background delivery.
 * Uses admin-sw.js (scope /admin/) — separate from merchant PWA push at scope /.
 */
export default function AdminPushBootstrap() {
  useEffect(() => {
    const sync = () => {
      if (!localStorage.getItem('admin_token')) return;
      if (getNotificationPermission() !== 'granted') return;
      syncAdminPushIfAuthenticated().catch(() => {});
    };

    sync();

    const heartbeat = async () => {
      if (!localStorage.getItem('admin_token') || getNotificationPermission() !== 'granted') return;
      try {
        const reg = await waitForAdminServiceWorkerRegistration();
        const sub = await reg.pushManager.getSubscription();
        if (sub?.endpoint) {
          await sendAdminPushHeartbeat(sub.endpoint);
        }
      } catch {
        // ignore
      }
    };

    const heartbeatInterval = window.setInterval(heartbeat, 5 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') sync();
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(heartbeatInterval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'ADMIN_PUSH_SUBSCRIPTION_CHANGED' && localStorage.getItem('admin_token')) {
        syncAdminPushIfAuthenticated().catch(() => {});
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  return null;
}
